'use client'

import { Search } from '@mui/icons-material'
import { Button, Chip, Stack, Typography } from '@mui/material'

import type { ReactElement } from 'react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { tagGroupNames, type VendorSearchState } from '../../states/useVendorSearchState'
import { DEFAULT_OPTION_VALUE } from '../supplier-filters/type/constants'

type ActiveChip = {
  key: string
  label: string
  onDelete: () => void
  icon?: ReactElement
}

const TAG_GROUPS = Object.values(tagGroupNames)

const SUPPLIER_TYPE_CLEAR_VALUE = 'all'

const getSupplierTypeLabel = (value: string, translate: (key: string) => string) => {
  const translationKey = `marketplace:${value}Option`
  const translated = translate(translationKey)
  if (translated !== translationKey) {
    return translated
  }
  return value.charAt(0).toUpperCase() + value.slice(1)
}

type ActiveSupplierFiltersProps = {
  filters: VendorSearchState['filters']
  search: VendorSearchState['search']
}

export const ActiveSupplierFilters = ({ filters, search }: ActiveSupplierFiltersProps) => {
  const { t } = useTranslation()

  const supplierType = (filters.supplierType as string | null | undefined) ?? DEFAULT_OPTION_VALUE

  const tagChips = useMemo(() => {
    const chips: ActiveChip[] = []

    TAG_GROUPS.forEach((group) => {
      const rawValues = filters[group.key]
      const values = Array.isArray(rawValues) ? rawValues : []

      values
        .filter((value): value is string => Boolean(value))
        .forEach((value) => {
          chips.push({
            key: `${group.key}:${value}`,
            label: value,
            onDelete: () => {
              const updatedValues = values.filter((item) => item !== value)
              search({
                [group.key]: updatedValues,
              })
            },
          })
        })
    })

    return chips
  }, [filters, search])

  const itemSearchChip: ActiveChip | null = filters.itemSearch
    ? {
        key: 'itemSearch',
        label: filters.itemSearch,
        icon: <Search fontSize="small" />,
        onDelete: () => {
          search({ itemSearch: '' })
        },
      }
    : null

  const supplierTypeChip: ActiveChip | null =
    supplierType && supplierType !== SUPPLIER_TYPE_CLEAR_VALUE
      ? {
          key: `supplierType:${supplierType}`,
          label: getSupplierTypeLabel(supplierType, t),
          onDelete: () => {
            search({
              supplierType: SUPPLIER_TYPE_CLEAR_VALUE,
            })
          },
        }
      : null

  const activeChips = [
    ...(itemSearchChip ? [itemSearchChip] : []),
    ...(supplierTypeChip ? [supplierTypeChip] : []),
    ...tagChips,
  ]

  if (!activeChips.length) {
    return null
  }

  const handleClearAll = () => {
    search({
      supplierType: SUPPLIER_TYPE_CLEAR_VALUE,
      itemSearch: '',
      [tagGroupNames.Occasion.key]: [],
      [tagGroupNames.Cuisine.key]: [],
      [tagGroupNames.Dietary.key]: [],
      [tagGroupNames.KeyDates.key]: [],
      [tagGroupNames.DiversityOfOwnership.key]: [],
      [tagGroupNames.BestFor.key]: [],
      highlightsTags: [],
    })
  }

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="body2" color="text.secondary" fontWeight={600}>
          {t('marketplace:activeFilters', 'Active filters')}
        </Typography>
        <Button variant="text" size="small" onClick={handleClearAll}>
          {t('common:clearAll')}
        </Button>
      </Stack>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {activeChips.map((chip) => (
          <Chip key={chip.key} icon={chip.icon} label={chip.label} onDelete={chip.onDelete} />
        ))}
      </Stack>
    </Stack>
  )
}
